import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import moment from 'moment';
import { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';
import { useTabActivityIndicator } from '../../../../../hooks/useTabActivityIndicator';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { invalidateAllContentCalendarSuggestions, useAdminAccountQuery } from '../../../../../queries';
import { useAccountProfileQuery } from '../../../../../queries/AccountProfile/useAccountProfileQuery';
import { useBrandingSettingsQuery } from '../../../../../queries/AccountProfile/useBrandingSettingsQuery';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { invalidateAllAIConversationFilterQueries } from '../../../../../queries/AIConversation/useAIConversationFilterQuery';
import { useApproveSuggestedContentMutation } from '../../../../../queries/ContentCalendarSuggestion/useApproveSuggestedContentMutation';
import {
	getApproveSuggestedContentPreviewCount,
	useApproveSuggestedContentPreviewMutation,
} from '../../../../../queries/ContentCalendarSuggestion/useApproveSuggestedContentPreviewMutation';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../components/ConfirmationDialog';

import { WarningIcon, WarningIconType } from '../../../../components/svgs/icons/WarningIcon';
import { AIConversationMessage } from '../../../../components-v2/ai/AIConversationMessage';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../../components-v2/ai/ChatConversation';
import {
	PromptInput,
	PromptInputAttachmentButton,
	PromptInputBody,
	PromptInputFooter,
	PromptInputSpeechButton,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
	type PromptInputMessage,
} from '../../../../components-v2/ai/PromptInput';
import {
	AI_CHAT_FILE_ACCEPT,
	AI_CHAT_MAX_FILE_SIZE,
	AI_CHAT_MAX_FILES,
	PromptInputAttachments,
	useAIChatAttachments,
} from '../../../../components-v2/ai/PromptInput/PromptInputAttachments';
import { ThinkingIndicator } from '../../../../components-v2/ai/ThinkingIndicator';
import { Button } from '../../../../components-v2/Button';
import { AiGeneratedContentRefineDialog } from '../../../ContentCalendar/AiGeneratedContentRefineDialog';
import { bs } from '../../../../styles/styles';
import { dispatchContentCalendarAccepted } from '../../../ContentCalendar/events';
import {
	AIChatAssistantMessage,
	removeErrorMessageFromConversationCache,
	UniversalAssistantLayout,
	useAIAssistantStreamingMutation,
} from '../';
import { s } from '../styles';

const ReplacePreviousSuggestionsOptions: IConfirmationDialogOption<boolean>[] = [
	{ isCta: true, representedObject: true, title: 'Replace previous suggestions' },
	{ isCta: false, representedObject: false, title: 'Keep both' },
	{ isCancel: true, representedObject: false, title: 'Cancel' },
];
import { ContentCalendarGenerationForm, type ContentCalendarGenerationRequest } from './ContentCalendarGenerationForm';
import { styleSheet } from './styles';
import { hasBrandingSettings } from '../../../../../utils/hasBrandingSettings';

const CONTENT_CALENDAR_ASSISTANT_NAME = 'ContentCalendarAgent';

interface ContentCalendarAssistantChatProps {
	impersonationContext: Api.IImpersonationContext;
	initialConversationId?: string;
	onUseAIEmail?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseContact?: (value: {
		firstName?: string;
		lastName?: string;
		email?: string;
		phoneNumber?: string;
		company?: string;
	}) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
}

const ContentCalendarAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: CONTENT_CALENDAR_ASSISTANT_NAME,
			impersonationContext,
		});
	};

	return (
		<ErrorBoundary
			fallback={
				<UniversalAssistantLayout onNewChat={handleNewChatClick}>
					<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
						<WarningIcon />
						<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
						<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick}>
							Start new chat
						</Button>
					</div>
				</UniversalAssistantLayout>
			}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function ContentCalendarAssistantChat(props: ContentCalendarAssistantChatProps) {
	return (
		<ContentCalendarAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
			<ContentCalendarAssistantChartInstance {...props} />
		</ContentCalendarAssistantChatErrorBoundary>
	);
}

export function ContentCalendarAssistantChartInstance({
	onClose,
	renderConversationStarters,
	impersonationContext,
	initialConversationId,
}: ContentCalendarAssistantChatProps) {
	const id = useId();
	const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('ContentCalendarAssistantChat');
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});
	const accountProfileQuery = useAccountProfileQuery({ impersonationContext });
	const contentProfile = accountProfileQuery.data;
	const brandingSettingsQuery = useBrandingSettingsQuery({ impersonationContext });
	// Keep this indeterminate (undefined) while the query is in flight so the form does not flash a
	// misleading "set up branding" hint to accounts that actually have branding configured.
	const accountHasBrandingSettings = brandingSettingsQuery.isLoading
		? undefined
		: hasBrandingSettings(brandingSettingsQuery.data);

	const { mutateAsync: createConversationAsync, isPending: isCreatingConversation } =
		useAIAssistantCreateConversationMutation({
			onSuccess: () => {
				invalidateAllAIConversationFilterQueries();
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
			},
		});

	// Approval is additive — remember which entries were already scheduled so their cards render read-only
	const [approvedEntryIds, setApprovedEntryIds] = useState<string[]>([]);
	const [previewEntry, setPreviewEntry] = useState<Api.ContentCalendarEntryToolCall | null>(null);

	useEffect(() => {
		if (initialConversationId) {
			setActiveConversationId(initialConversationId);
			setApprovedEntryIds([]);
			setPreviewEntry(null);
		}
	}, [initialConversationId]);

	const handleNewChatClick = () => {
		setActiveConversationId(null);
		setApprovedEntryIds([]);
		setPreviewEntry(null);
	};

	const [pendingApproval, setPendingApproval] = useState<{
		count: number;
		suggestionIds?: string[];
		toolCall: Api.ContentCalendarResourceToolCall;
	} | null>(null);

	const { mutate: approveSuggestedContent, isPending: isApprovingSuggestedContent } =
		useApproveSuggestedContentMutation({
			onSuccess: (_data, variables) => {
				const newlyApproved = variables.suggestionIds ?? [];
				if (newlyApproved.length) {
					setApprovedEntryIds(prev => [...prev, ...newlyApproved.filter(entryId => !prev.includes(entryId))]);
				}
				invalidateAllContentCalendarSuggestions();
				dispatchContentCalendarAccepted(variables.calendarId);
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logger.logApiError(`Approve-ContentCalendar-Error`, error);
			},
		});

	const approveCalendar = (
		toolCall: Api.ContentCalendarResourceToolCall,
		selectedEntryIds: string[] | undefined,
		replacePreviousSuggestions: boolean
	) => {
		approveSuggestedContent({
			calendarId: toolCall.calendarId,
			contentCalendarSuggestion: toolCall,
			impersonationContext,
			replacePreviousSuggestions,
			suggestionIds: selectedEntryIds,
		});
	};

	const { mutate: previewPreviousSuggestions, isPending: isPreviewingPreviousSuggestions } =
		useApproveSuggestedContentPreviewMutation({
			onSuccess: (preview, variables) => {
				const previousCount = getApproveSuggestedContentPreviewCount(preview);
				if (previousCount === 0) {
					approveCalendar(variables.toolCall, variables.suggestionIds, false);
					return;
				}

				setPendingApproval({
					count: previousCount,
					suggestionIds: variables.suggestionIds,
					toolCall: variables.toolCall,
				});
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logger.logApiError(`Approve-ContentCalendar-Preview-Error`, error);
			},
		});

	const handleApproveContentCalendar = (toolCall: Api.ContentCalendarResourceToolCall, selectedEntryIds?: string[]) => {
		previewPreviousSuggestions({
			calendarId: toolCall.calendarId,
			impersonationContext,
			suggestionIds: selectedEntryIds,
			toolCall,
		});
	};

	const handleReplaceConfirmationClose = (option?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		const approval = pendingApproval;
		setPendingApproval(null);

		if (canceled || !option || !approval) {
			return;
		}

		approveCalendar(approval.toolCall, approval.suggestionIds, option.representedObject === true);
	};

	const handlePreviewCalendarEntry = (entry: Api.ContentCalendarEntryToolCall) => {
		setPreviewEntry(entry);
	};

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(activeConversationId),
		id: activeConversationId,
		staleTime: Infinity,
		impersonationContext,
	});

	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: CONTENT_CALENDAR_ASSISTANT_NAME,
	});
	const isStreaming = aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming;
	const attachmentsController = useAIChatAttachments({ impersonationContext });
	const { uploadedById, isUploading, reset: resetAttachments } = attachmentsController;
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	useTabActivityIndicator(aiAssistantStreamingMutation.status);

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		if (!activeConversationId) return;
		removeErrorMessageFromConversationCache(activeConversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId: activeConversationId,
			chatRequest: { ...chatRequest, includeProgress: true },
			impersonationContext,
		});
	};

	const handleGenerate = async ({
		contentTypes,
		instructions,
		customizeExistingContent,
		fileAttachmentIds,
	}: ContentCalendarGenerationRequest) => {
		const startDate = moment();
		const endDate = moment().add(90, 'days');

		let conversationId = activeConversationId;
		if (!conversationId) {
			try {
				const conversation = await createConversationAsync({
					assistantName: CONTENT_CALENDAR_ASSISTANT_NAME,
					impersonationContext: impersonationContextRef.current,
				});
				setActiveConversationId(conversation.id);
				conversationId = conversation.id;
			} catch {
				return;
			}
		}

		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				_type: 'AIActionRequest',
				includeProgress: true,
				action: {
					_type: 'ContentCalendarGenerationAction',
					startDate: startDate.format('YYYY-MM-DD'),
					endDate: endDate.format('YYYY-MM-DD'),
					contentTypes: contentTypes.length > 0 ? contentTypes : undefined,
					instructions: instructions.trim() || undefined,
					fileAttachmentIds: fileAttachmentIds.length > 0 ? fileAttachmentIds : undefined,
					customizeExistingContent,
					supportsIndividualScheduling: true,
				},
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};

	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};

	const handleTranscriptionChange = () => {
		textareaRef.current?.form?.requestSubmit();
	};

	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!activeConversationId || !text.trim()) return;
		if (isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		if (isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		const contents: Api.AIChatContent[] = [
			{ _type: 'AITextContent', text } as Api.AITextContent,
			...Object.values(uploadedById).map(
				attachment => ({ _type: 'AIFileContent', fileAttachmentId: attachment.id }) as Api.AIFileContent
			),
		];
		removeErrorMessageFromConversationCache(activeConversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: activeConversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId: activeConversationId,
			chatRequest: {
				includeProgress: true,
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						contents,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
		resetAttachments();
	};

	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						<div className={css(styleSheet.assistantDefaultMessageContent)}>
							<span>
								Hi {userSession.user.firstName}! What content would you like planned for{' '}
								{`${accountQuery.data?.companyName ?? 'this account'}`}?
							</span>

							<ContentCalendarGenerationForm
								onGenerate={handleGenerate}
								impersonationContext={impersonationContext}
								disabled={isCreatingConversation || isStreaming}
								contentProfile={contentProfile}
								accountFeatures={impersonationContext.account.features}
								accountHasBrandingSettings={accountHasBrandingSettings}
							/>
						</div>
					</AIChatAssistantMessage>

					{renderConversationStarters &&
					activeConversationId &&
					!aiConversationChatMessageQuery.data?.length &&
					!isStreaming
						? renderConversationStarters(activeConversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onErrorTryAgain={handleTryAgain}
								onApproveContentCalendar={handleApproveContentCalendar}
								conversationId={activeConversationId}
								onApproveContentCalendarLoading={isApprovingSuggestedContent || isPreviewingPreviousSuggestions}
								approvedContentCalendarEntryIds={approvedEntryIds}
								onPreviewCalendarEntry={handlePreviewCalendarEntry}
								impersonationContext={impersonationContext}
							/>
						);
					})}
					{isCreatingConversation || aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator showTime={false} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			{activeConversationId ? (
				<div className={css(s.inputContainer)}>
					<PromptInput
						onSubmit={handlePromptSubmit}
						accept={AI_CHAT_FILE_ACCEPT}
						maxFiles={AI_CHAT_MAX_FILES}
						maxFileSize={AI_CHAT_MAX_FILE_SIZE}
						onError={err => toast.error(err.message)}
						multiple
					>
						<PromptInputAttachments controller={attachmentsController} />
						<PromptInputBody>
							<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
						</PromptInputBody>
						<PromptInputFooter>
							<PromptInputTools>
								<PromptInputAttachmentButton />
								<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
							</PromptInputTools>
							<PromptInputSubmit
								status={aiAssistantStreamingMutation.status}
								onClick={handlePromptSubmitClick}
								disabled={isUploading}
							/>
						</PromptInputFooter>
					</PromptInput>
				</div>
			) : null}

			<ConfirmationDialog
				icon={<WarningIcon type={WarningIconType.Blue} />}
				modalProps={{
					isOpen: !!pendingApproval,
					onRequestClose: handleReplaceConfirmationClose,
				}}
				options={ReplacePreviousSuggestionsOptions}
				title={
					<div>
						<div>Replace previous suggestions?</div>

						<p className={css(styleSheet.replacePreviousSuggestionsText)}>
							This date range already has {pendingApproval?.count} AI-generated{' '}
							{pendingApproval?.count === 1 ? 'suggestion' : 'suggestions'}. You can replace{' '}
							{pendingApproval?.count === 1 ? 'it' : 'them'} with the new content, or keep both. Already-scheduled
							content will not be affected.
						</p>
					</div>
				}
			/>

			{previewEntry && (
				<AiGeneratedContentRefineDialog
					entry={previewEntry}
					open={!!previewEntry}
					onOpenChange={open => !open && setPreviewEntry(null)}
					impersonationContext={impersonationContext}
				/>
			)}
		</UniversalAssistantLayout>
	);
}
